import {
  DocumentClassAttributes,
  ExpeditionConditionAttributes,
  OrderAttributes,
} from '@albatrosdeveloper/ave-models-package';
import {
  CombinedFilter,
  buildQuery,
  where,
  andWhere,
  Ops,
  noop,
  orderBy,
  page,
  OrderVariant,
} from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment as env } from '../../../../../environments/environment';
import {
  IAPIItemResult,
  IAPIListResult,
  IRequestResult,
} from '../../../../common/interfaces';
import { AbstractService } from '../../../../services/abstract.service';

import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';
import PaymentConditionAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/masters/payment-condition/payment-condition.entity';
import { BusinessPartnerService } from '../../business-partners/business-partner/business-partner.service';
import { OpentItemsService } from '../../reports/historic/open-item/open-items.service';
import { IOverduePaymentRestrictionResult, ShipToInterface } from './interfaces/ship-to.interface';
import { PricingResponse } from './interfaces/document-item.interface';
import { DeliveryRouteRequest, DeliveryRouteResolution } from './interfaces/delivery-route.interface';
import {
  PackagingRequirementsRequest,
  PackagingRequirementsResponse,
} from './interfaces/pack-loan-request.interface';

const businessPartnerUrl = env.API_BUSINESS_PARTNER_URL + 'api/';
const orderUrl = env.API_AVE_ORDER_URL + 'api/';
const configurationUrl = env.API_CONFIGURATION_URL + 'api/';
const documentUrl = env.API_DOCUMENT_URL + 'api/';
const masterUrl = env.API_UNIT_MEASURE + 'api/';
const materialUrl = env.API_MATERIAL_URL + 'api/';
const priceListUrl = env.API_MATERIAL_PRICE_LIST_URL + 'api/';
const authorizationURL = env.API_AVE_AUTHORIZATION_URL + 'api/';
const moduleName = 'shipments-preparation/';

@Injectable({
  providedIn: 'root',
})
export class DocumentGenerateService extends AbstractService<
  OrderAttributes,
  Partial<OrderAttributes>,
  Partial<OrderAttributes>
> {
  constructor(
    http: HttpClient,
    private businessPartnerService: BusinessPartnerService,
    private opentItemsService: OpentItemsService
  ) {
    super(orderUrl, moduleName, http);
  }

  // Clases de documento filtradas por tipo (PEDIDO / COTIZACION)
  fetchDocumentClasses(
    filter?: CombinedFilter<DocumentClassAttributes>
  ): Observable<IAPIListResult<DocumentClassAttributes>> {
    let API_URL: string = `${documentUrl}document_class/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<DocumentClassAttributes>>(API_URL);
  }

  fetchSalesOfficesByDocumentClass(
    documentClassId: string,
    filter?: CombinedFilter<SalesOfficeAttributes>
  ): Observable<IAPIListResult<SalesOfficeAttributes>> {
    let API_URL: string = `${configurationUrl}sales-office/byDocumentClass/${documentClassId}`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<SalesOfficeAttributes>>(API_URL);
  }

  fetchSalesOffices(
    filter?: CombinedFilter<SalesOfficeAttributes>
  ): Observable<IAPIListResult<SalesOfficeAttributes>> {
    let API_URL: string = `${configurationUrl}sales-office/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<SalesOfficeAttributes>>(API_URL);
  }

  fetchExpeditionCondition(
    filter?: CombinedFilter<ExpeditionConditionAttributes>
  ): Observable<IAPIListResult<ExpeditionConditionAttributes>> {
    let API_URL: string = `${masterUrl}expedition_condition/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<ExpeditionConditionAttributes>>(API_URL);
  }

  fetchPaymentConditions(
    filter?: CombinedFilter<PaymentConditionAttributes>
  ): Observable<IAPIListResult<PaymentConditionAttributes>> {
    let API_URL: string = `${masterUrl}payment_condition/filter`;
    if (filter) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    return this.http.get<IAPIListResult<PaymentConditionAttributes>>(API_URL);
  }

  // Solicitantes (ship-to) con sus pagadores por cadena de distribución
  fetchShipTo(shipToRequest: ShipToInterface): Observable<any> {
    const API_URL: string = `${businessPartnerUrl}business-partner/get_shiptos_with_soldtos_by_query`;
    return this.http.post<IAPIItemResult<any>>(API_URL, shipToRequest);
  }

  // Solicitantes sin depender de la oficina de venta (traen su routingSalesOffice)
  findShipTosWithoutOffice(
    search?: string,
    salesOfficeId?: string,
    department?: string,
    routeId?: number,
    page = 1,
    size = 25,
    companyIds?: number[]
  ): Observable<any> {
    const API_URL: string = `${businessPartnerUrl}business-partner/get_shiptos_without_office`;
    // This endpoint filters by the NUMERIC company id — pass company.id, NOT the
    // Mongo _id, or the backend matches nothing.
    //
    // No fallback to a build-time default when the company is still unknown: that
    // company may be outside the user's allowed set, and its ship-tos would leak
    // into the autocomplete before anything is picked. Return nothing instead and
    // let the caller re-ask once a company is selected (same rule as
    // asset-control.component.ts).
    if (!companyIds?.length) {
      return of({ data: [] });
    }
    const body: any = {
      companyIds,
      search: search ?? '',
      page,
      size,
    };
    if (salesOfficeId) body.salesOfficeId = salesOfficeId;
    if (department) body.department = department;
    if (routeId != null) body.routeId = routeId;
    return this.http.post<IAPIItemResult<any>>(API_URL, body);
  }

  // Vendedores (fuerza de ventas) por login de preventa
  findPreSaleAllUserLogin(data): Observable<IAPIListResult<any>> {
    const API_URL: string = masterUrl + 'master_user/presale/user';
    return this.http.post<IAPIListResult<any>>(API_URL, data);
  }

  // Materiales con stock, paginado y con búsqueda por código/sku/nombre/etc
  findMaterialsWithStock(
    search: string,
    distributionChainId: number | null,
    divisionIds: number[],
    pageIndex = 1,
    size = 50
  ): Observable<any> {
    const query = buildQuery<any>(
      where('statusId', StatusEnum.ACTIVE),
      andWhere('_deleted', Ops.eq(false)),
      divisionIds?.length ? andWhere('divisionId', Ops.in(...divisionIds)) : noop(),
      distributionChainId != null ? andWhere('distributionChainIds', Ops.eq(distributionChainId)) : noop(),
      orderBy('code', OrderVariant.Asc),
      page(pageIndex, size)
    );
    const API_URL: string = `${materialUrl}view_material_stock/withCountV2?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.post<any>(API_URL, {
      search: search ?? '',
      categoryCode: '0',
      withStock: null,
      warehouseId: null,
    });
  }

  // Artículos vendibles para generación de documentos: merge almacén ∩ clase de documento + precio embebido.
  // background evita el overlay global: el autocomplete busca en cada tecla y no debe tapar la pantalla
  findDocumentGeneratorMaterials(
    body: {
      documentClassId: string;
      warehouseId: string;
      warehouseIds?: string[];
      salesOfficeId?: string;
      businessPartnerId?: string;
      currencyId?: string;
      search?: string;
      page?: number;
      size?: number;
    },
    background = false
  ): Observable<any> {
    const API_URL: string = `${materialUrl}document-generator/materials`;
    return this.http.post<any>(
      API_URL,
      body,
      background ? { headers: { 'x-ave-ctx': 'background' } } : {}
    );
  }

  // Pedidos y productos sugeridos del solicitante (filtros y paginación independientes por sección)
  findSuggested(body: any): Observable<any> {
    const API_URL: string = `${orderUrl}order/suggested`;
    // x-ave-ctx: background evita el overlay global; el panel muestra su propio skeleton
    return this.http.post<IAPIItemResult<any>>(API_URL, body, {
      headers: { 'x-ave-ctx': 'background' },
    });
  }

  findMyDailyTransactions(userLoginId: string, page = 1, dayStart?: string, search?: string): Observable<any> {
    const API_URL: string = `${orderUrl}order/my-daily-transactions`;
    const body: any = { userLoginId, page };
    if (dayStart) body.dayStart = dayStart;
    if (search && search.trim() !== '') body.search = search.trim();
    return this.http.post<IAPIItemResult<any>>(API_URL, body, {
      headers: { 'x-ave-ctx': 'background' },
    });
  }

  printDocumentCopy(orderId: string): Observable<Blob> {
    const API_URL: string = `${env.API_DOCUMENT_EXPORT_URL}order-pdf/copy/${orderId}`;
    const params = {
      format: 'letter',
      landscape: 'false',
      scale: '1',
      printBackground: 'true',
      marginTop: '10mm',
      marginBottom: '10mm',
    };
    return this.http.get(API_URL, { params, responseType: 'blob' });
  }

  // Precio completo por cliente (XP01 + descuentos XD0x + escalas + bono XG01)
  fetchPrice(dataRequest: any, background = false): Observable<PricingResponse> {
    const API_URL: string = `${priceListUrl}sequence-reading/pricing`;
    return this.http.post<PricingResponse>(
      API_URL,
      dataRequest,
      background ? { headers: { 'x-ave-ctx': 'background' } } : {}
    );
  }

  // Proforma en PDF: se arma en el formulario y se manda en el body (no consulta base de datos)
  generateProforma(body: any): Observable<Blob> {
    const API_URL: string = `${env.API_DOCUMENT_EXPORT_URL}order-pdf/proforma`;
    const params = {
      format: 'letter',
      landscape: 'false',
      scale: '1',
      printBackground: 'true',
      marginTop: '10mm',
      marginBottom: '10mm',
    };
    return this.http.post(API_URL, body, { params, responseType: 'blob' });
  }

  // Creación de los documentos (pedido y cotización usan el mismo endpoint; el tipo va en cada orden).
  // El formulario puede particionar, así que orders llega con uno o varios documentos
  generateDocument(body: {
    confirmed: boolean;
    orders: any[];
    attachments?: { content: string; fileName: string }[];
    lineAttachments?: {
      orderIndex: number;
      position: number;
      files: { content: string; fileName: string }[];
    }[];
    // etiqueta por documento, en el mismo orden que orders, para identificarlos si la creación falla
    documentLabels?: string[];
  }): Observable<any> {
    const API_URL: string = `${orderUrl}shipments-preparation/validate-and-create-with-attachments`;
    return this.http.post<any>(API_URL, body);
  }

  // Rutas de entrega candidatas y su fecha sugerida; background evita el overlay global,
  // pero en el flujo de guardado el usuario espera y sí debe verlo
  resolveDeliveryRoutes(
    body: DeliveryRouteRequest,
    background = true
  ): Observable<IRequestResult<DeliveryRouteResolution>> {
    const API_URL: string = `${orderUrl}delivery-route/resolve`;
    return this.http.post<IRequestResult<DeliveryRouteResolution>>(
      API_URL,
      body,
      background ? { headers: { 'x-ave-ctx': 'background' } } : {}
    );
  }

  // Materiales de empaque y contenedores que exigen los artículos del documento
  resolvePackagingRequirements(
    body: PackagingRequirementsRequest,
    background = true
  ): Observable<IRequestResult<PackagingRequirementsResponse>> {
    const API_URL: string = `${orderUrl}document-generate/packaging-requirements`;
    return this.http.post<IRequestResult<PackagingRequirementsResponse>>(
      API_URL,
      body,
      background ? { headers: { 'x-ave-ctx': 'background' } } : {}
    );
  }

  // Stock del material en los almacenes de la oficina de venta
  fetchStockBySalesOffice(salesOfficeId: string, materialId: string): Observable<any> {
    const API_URL: string = `${materialUrl}material_stock/by-sales-office`;
    return this.http.post<any>(API_URL, { salesOfficeId, materialId }, {
      headers: { 'x-ave-ctx': 'background' },
    });
  }

  // Igual que el anterior pero para varias líneas a la vez, para no emitir una petición por artículo
  fetchStockBySalesOfficeBatch(salesOfficeId: string, materialIds: string[]): Observable<any> {
    const API_URL: string = `${materialUrl}material_stock/by-sales-office-batch`;
    return this.http.post<any>(API_URL, { salesOfficeId, materialIds }, {
      headers: { 'x-ave-ctx': 'background' },
    });
  }

  // Componentes de una lista de materiales (BOM) por código de lista
  fetchMaterialListComponents(listCode: string): Observable<any> {
    const query = buildQuery<any>(
      where('parentMaterialList.code', Ops.eq(listCode)),
      andWhere('statusId', StatusEnum.ACTIVE),
      andWhere('_deleted', Ops.eq(false)),
      page(1, 500)
    );
    const API_URL: string = `${materialUrl}material_list_component/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<any>(API_URL, { headers: { 'x-ave-ctx': 'background' } });
  }

  fetchApplications(applicationId: string, subApplicationId: string): Observable<any> {
    const API_URL: string = `${authorizationURL}applications/get_app?applicationId=${applicationId}&subApplicationId=${subApplicationId}`;
    return this.http.get<IAPIItemResult<any>>(API_URL);
  }

  fetchCurrencyFactor(currencySourceId: string, currencyTargetId: string): Observable<any> {
    const API_URL: string = `${masterUrl}currency/get_factor?currencySourceId=${currencySourceId}&currencyTargetId=${currencyTargetId}`;
    return this.http.get<IAPIItemResult<any>>(API_URL);
  }

  // Verifica si aplica restricción de pago por morosidad (solo Contado)
  shouldRestrictToCashOnly(
    businessPartnerMongoId: string,
    distributionChainId: string
  ): Observable<IOverduePaymentRestrictionResult> {
    if (!businessPartnerMongoId || !distributionChainId) {
      return of({ applies: false });
    }
    return this.businessPartnerService.findById(businessPartnerMongoId).pipe(
      switchMap((res: IRequestResult<BusinessPartnerAttributes>) => {
        const bp = res?.data;
        if (!bp?.distributionChains?.length) return of({ applies: false });
        const distChainIdStr = distributionChainId.toString();
        const matchingChain = bp.distributionChains.find(
          (dc: any) =>
            dc._id?.toString() === distChainIdStr ||
            dc.distributionChain?._id?.toString() === distChainIdStr ||
            dc.distributionChainId?.toString() === distChainIdStr
        );
        if (matchingChain?.onlyCashIfOverdueInvoices !== true) return of({ applies: false });
        const overdueThresholdDate = new Date();
        overdueThresholdDate.setDate(overdueThresholdDate.getDate() - 1);
        const yyyy = overdueThresholdDate.getFullYear().toString().padStart(4, '0');
        const mm = (overdueThresholdDate.getMonth() + 1).toString().padStart(2, '0');
        const dd = overdueThresholdDate.getDate().toString().padStart(2, '0');
        // No company filter: the check asks whether THIS business partner has any
        // overdue invoice at all, and `findByBusinessPartner` already keys on the
        // partner's Mongo id. The previous `companyId: env.companyId` never
        // reached the query anyway — that builder only emits the `company.id`
        // clause for a non-empty ARRAY, so a scalar was silently dropped.
        const dataToFilter = {
          expirationStartDate: '1970-01-01',
          expirationEndDate: `${yyyy}-${mm}-${dd}`,
        };
        return this.opentItemsService
          .findByBusinessPartner(
            businessPartnerMongoId,
            { currentPage: 1, limitPerPage: 1, search: '' },
            dataToFilter
          )
          .pipe(
            map((r) => ({ applies: !!(r?.data?.data?.length && r.data.data.length > 0) })),
            catchError((err) => throwError(() => err))
          );
      }),
      catchError((err) => throwError(() => err))
    );
  }
}
